// ╔══════════════════════════════════════════════════════════════╗
// ║  Payslips browser — list all payslips across runs              ║
// ║  Filter by month + employee · download PDF per row             ║
// ╚══════════════════════════════════════════════════════════════╝

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useToast } from '../components/Toast.jsx';
import { fmtAR, fmtMoney } from '../lib/format.js';
import { Avatar, Badge, Btn, Card, Field, Input } from '../components/ui.jsx';
import Icon from '../components/Icon.jsx';

export default function PayslipsPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [search, setSearch] = useState('');

  // Find the run for that month, then list its payslips
  const { data: runsData } = useQuery({
    queryKey: ['payroll', 'runs'],
    queryFn: () => api.get('/payroll/runs').then((r) => r.data),
  });

  const matchingRun = (runsData?.runs ?? []).find(
    (r) => r.month?.startsWith(month),
  );

  const { data, isLoading } = useQuery({
    queryKey: ['payroll', 'run', matchingRun?.id],
    queryFn: () =>
      matchingRun
        ? api.get(`/payroll/runs/${matchingRun.id}`).then((r) => r.data)
        : null,
    enabled: !!matchingRun,
  });

  const allSlips = data?.payslips ?? [];
  const slips = allSlips.filter((s) => {
    if (!search) return true;
    return (
      s.employeeName?.includes(search) ||
      s.employeeId?.includes(search) ||
      s.deptId?.includes(search)
    );
  });

  function viewHtml(slipId) {
    navigate(`/payslips/${slipId}`);
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 fade-up">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-brand-500 font-bold">
            كشوفات الرواتب
          </div>
          <h1 className="text-2xl md:text-3xl font-black mt-1">كشوف رواتب الموظفين</h1>
          <p className="text-sm text-ink-400 mt-1">
            استعرض، اطبع، أو حمّل كشف راتب أى موظف
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-3">
          <Field label="الشهر">
            <Input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              dir="ltr"
              className="w-44"
            />
          </Field>
          <div className="relative flex-1 min-w-[200px]">
            <Icon name="search" className="absolute top-3 right-3 text-ink-400" size={16} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="ابحث بالاسم أو الرقم أو القسم…"
              className="w-full h-10 px-4 pe-10 rounded-xl bg-ink-50 dark:bg-ink-800 ring-1 ring-ink-200 dark:ring-ink-700 focus:ring-2 focus:ring-brand-400 outline-none text-sm"
            />
          </div>
          {matchingRun && (
            <Badge
              tone={
                matchingRun.status === 'approved'
                  ? 'success'
                  : matchingRun.status === 'calculated'
                  ? 'warn'
                  : 'neutral'
              }
              dot
            >
              {matchingRun.status === 'approved'
                ? 'معتمد'
                : matchingRun.status === 'calculated'
                ? 'محسوب — لم يُعتمد'
                : 'مسودة'}
            </Badge>
          )}
        </div>
      </Card>

      {!matchingRun ? (
        <Card className="p-12 text-center">
          <Icon name="wallet" size={48} className="mx-auto text-ink-300 mb-3" />
          <h3 className="font-bold text-ink-500">لا توجد دورة رواتب لشهر {month}</h3>
          <p className="text-xs text-ink-400 mt-1">
            تأكد إن دورة الرواتب اتشغّلت من صفحة "الرواتب".
          </p>
        </Card>
      ) : isLoading ? (
        <Card className="p-8 text-center text-ink-400">جاري التحميل…</Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-ink-50 dark:bg-ink-800/40 text-xs text-ink-500">
                <tr>
                  {['#', 'الموظف', 'القسم', 'الإجمالي', 'الخصومات', 'الصافي', 'إجراءات'].map((h, i) => (
                    <th key={i} className="text-start font-semibold px-4 py-3 whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-soft">
                {slips.length === 0 && (
                  <tr>
                    <td colSpan="7" className="p-8 text-center text-ink-400">
                      لا توجد كشوف
                    </td>
                  </tr>
                )}
                {slips.map((s) => {
                  const totalDed =
                    Number(s.absenceDeduction) +
                    Number(s.lateDeduction) +
                    Number(s.advanceDeduction) +
                    Number(s.otherDeductions) +
                    Number(s.insurance) +
                    Number(s.tax);
                  return (
                    <tr key={s.id} className="hover:bg-ink-50 dark:hover:bg-ink-800/40">
                      <td className="px-4 py-3 font-mono text-[11px] text-ink-400">{s.employeeId}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <Avatar name={s.employeeName} size={32} />
                          <span className="font-semibold">{s.employeeName}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">{s.deptId ?? '—'}</td>
                      <td className="px-4 py-3 tabular font-semibold">{fmtMoney(s.gross)}</td>
                      <td className="px-4 py-3 tabular text-rose-600">-{fmtMoney(totalDed)}</td>
                      <td className="px-4 py-3 tabular font-extrabold">{fmtMoney(s.net)}</td>
                      <td className="px-4 py-3">
                        <Btn size="sm" variant="outline" icon="file" onClick={() => viewHtml(s.id)}>
                          عرض
                        </Btn>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {slips.length > 0 && (
                <tfoot>
                  <tr className="bg-brand-50 dark:bg-brand-950/30 font-bold">
                    <td colSpan="5" className="px-4 py-3 text-end">
                      الإجمالى ({fmtAR(slips.length)} موظف)
                    </td>
                    <td className="px-4 py-3 tabular text-brand-700 dark:text-brand-300">
                      {fmtMoney(slips.reduce((a, s) => a + Number(s.net), 0))}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
